import { z } from "zod";
import { nowadaysApiUrl } from "@/utils/url-utils";
import { SupportThreadSnapshot } from "./types";

const historyMessageSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("user"), id: z.string(), ts: z.string(), text: z.string(), createdAt: z.string() }),
  z.object({
    kind: z.literal("admin"),
    id: z.string(),
    ts: z.string(),
    text: z.string(),
    // Kept loose here: `toSafeAdminMessage` filters images to https before anything renders.
    images: z.array(z.object({ url: z.string(), width: z.number().optional(), height: z.number().optional() })),
    sender: z.object({ name: z.string(), avatarUrl: z.string().nullable() }),
    createdAt: z.string(),
  }),
]);

const snapshotSchema: z.ZodType<SupportThreadSnapshot> = z.object({
  thread: z.object({ status: z.enum(["open", "closed"]), messages: z.array(historyMessageSchema) }).nullable(),
  unread: z.number().int().nonnegative(),
});

/**
 * Reads the planner's current support thread: its stored messages, status, and unread admin replies.
 *
 * @param accessToken - Supabase JWT for the Authorization header.
 * @returns The thread snapshot, or null when the call failed or answered outside the contract,
 *   so the caller keeps what it already shows.
 */
export const fetchSupportThread = async (accessToken: string): Promise<SupportThreadSnapshot | null> => {
  try {
    const response = await fetch(nowadaysApiUrl("/support-chat/thread"), {
      headers: { Authorization: `Bearer ${accessToken}` },
      cache: "no-store",
    });
    if (!response.ok) return null;
    const parsed = snapshotSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

/**
 * Marks the planner's current support thread read. Idempotent; the backend tells
 * every other tab through the `support-chat:read` socket event.
 *
 * @param accessToken - Supabase JWT for the Authorization header.
 * @returns Whether the backend accepted it.
 */
export const markSupportThreadRead = async (accessToken: string): Promise<boolean> => {
  try {
    const response = await fetch(nowadaysApiUrl("/support-chat/read"), {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.ok;
  } catch {
    return false;
  }
};
